import cn from 'classnames';
import { memo, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNeedCapture } from '../../../../state';
import AgeSelector from '../age-selector';
import BreedSelector from '../breed-selector';
import FoodTasteSelector from '../food-type-and-taste-selector';
import NeedsSelector from '../needs-selector';

interface ContentSectionProps {
  isMobile: boolean;
  petType: 'dog' | 'cat';
}

/**
 * Content Section of the NeedCapture component
 */
const ContentSection: React.FC<ContentSectionProps> = ({
  isMobile,
  petType,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { hasInteracted } = useNeedCapture();

  useEffect(() => {
    if (isMobile && hasInteracted && ref.current) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isMobile, hasInteracted]);

  return (
    <div
      className={cn(
        'text-text-dark-bg-primary relative md:-top-6 lg:max-w-2xl lg:pt-16',
        petType === 'dog' ? 'bg-dog-base' : 'bg-cat-base'
      )}
    >
      <h3 className="mindset-32 lg:mindset-48">
        <FormattedMessage
          id="landingpage:need-capture:title"
          values={{ br: isMobile ? <br /> : null }}
        />
      </h3>
      <p className="text-text-dark-bg-secondary mt-2 lg:font-medium">
        <FormattedMessage
          id={`landingpage:need-capture:sub-title:${petType}`}
        />
      </p>
      <div
        className="text-text-dark-bg-primary flex flex-col gap-3.5 pt-6 font-black lg:flex-row lg:flex-wrap lg:pb-5"
        ref={ref}
      >
        <AgeSelector isMobile={isMobile} petType={petType} />
        {petType === 'dog' && <BreedSelector isMobile={isMobile} />}
        <NeedsSelector isMobile={isMobile} petType={petType} />
        {petType === 'cat' && <FoodTasteSelector isMobile={isMobile} />}
      </div>
    </div>
  );
};

export default memo(ContentSection);
